import React, {Suspense, SyntheticEvent, useEffect, useState} from 'react';
import {SnackbarCloseReason, StyledEngineProvider, ThemeProvider} from "@mui/material";
import {theme} from './theme/theme';
import store from './redux/store';
import {Provider} from 'react-redux';
import {skipToken} from "@reduxjs/toolkit/query";
import {useAppDispatch, useAppSelector, useAuth} from "./redux/hooks";
import {Route, Routes, BrowserRouter} from "react-router-dom";
import LocationSync from './components/common/LocationSync';
import LoginModal from './components/authentication/LoginModal'
import Notification from "./components/common/Notification";
import ui from "./redux/reducers/uiReducer";
import ResidentPortalError from "./components/pages/ResidentPortalError";
import Dashboard from "./components/pages/Dashboard";
import {useGetUsersMeQuery} from "./redux/residentPortalApi";
import LoadingCover from "./components/common/LoadingCover";
import CssBaseline from '@mui/material/CssBaseline';
// @ts-ignore
import HttpsRedirect from 'react-https-redirect';
import { isNative } from './utils/platform';
import PropertyPage from './components/pages/PropertyPage';
import AdminPortal from "./components/pages/AdminPortal";
import VisitorManagementDashboard from "./components/vms/VisitorManagementDashboard";
import BnbGuestPortal from "./components/vms/BnbGuestPortal";
import PrivateRoute from "./components/common/PrivateRoute";
import ResetPasswordPage from "./components/authentication/resetPassword/ResetPasswordPage";
import NotFound from "./components/pages/NotFound";
import PaymentReturn from "./components/pages/PaymentReturn";
import { Analytics } from '@vercel/analytics/react';
import PaymentReturnHandler from './components/payment/PaymentReturnHandler';
import config from "./config";
import LogRocket from "logrocket";
import { getToken } from './utils/tokenStorage';


const App = () => {
  const [hasToken, setHasToken] = useState<boolean | null>(null);
  const {user} = useAuth()
  const dispatch = useAppDispatch();

  const actionNotification = useAppSelector(state => state.ui.actionNotification)
  const closeNotification = (event: Event | SyntheticEvent<any, Event>, reason: SnackbarCloseReason) => {
    if (reason === 'clickaway' || reason === 'timeout') {
      dispatch(ui.actions.removeActionNotification())
    }
  }

  useEffect(() => {
    if (config.logrocket) {
      LogRocket.init('sq4cty/residents-portal');
      if (user && user.id && user.name && user.email) {
        LogRocket.identify(user.id , { name: user.name, email: user.email });
      }
    }
  }, [user]);

  useEffect(() => {
    let isMounted = true;

    getToken()
      .then((token) => {
        if (isMounted) {
          setHasToken(Boolean(token));
        }
      })
      .catch(() => {
        if (isMounted) {
          setHasToken(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [user]);

  const shouldSkip = (hasToken && !user) ? undefined : skipToken;
  const {isLoading} = useGetUsersMeQuery(shouldSkip);
  if (isLoading || hasToken === null) return <LoadingCover/>

  return (
    <ThemeProvider theme={theme}>
      <StyledEngineProvider injectFirst>
        <ResidentPortalError className={`App`}>
          <Suspense fallback={<LoadingCover/>}>
            {actionNotification && (
              <Notification
                open={actionNotification.show}
                title={actionNotification.notification.title}
                message={actionNotification.notification.description}
                onClose={closeNotification}
                position={{vertical: 'bottom', horizontal: 'left'}}
              />
            )
            }
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/property/:uuid" element={<PrivateRoute element={<PropertyPage />} roles={['user', 'admin', 'residents-support']} />} />
              <Route path="/admin/*" element={<PrivateRoute element={<AdminPortal />} roles={['admin', 'residents-support']} />} />
              <Route path="/visitors" element={<PrivateRoute element={<VisitorManagementDashboard />} roles={['user']} />} />
              <Route path="/bnb-guest-portal" element={<BnbGuestPortal />} />
              <Route path="/payments/return" element={<PaymentReturn />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <LoginModal/>
          <PaymentReturnHandler />
          <Analytics />
        </ResidentPortalError>
      </StyledEngineProvider>
    </ThemeProvider>
  );
}

const Wrapper = () => {
  return (
    <Provider store={store}>
      <CssBaseline />
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          {isNative() ? (
            // Native WebView: HttpsRedirect breaks inside Capacitor – skip it
            <>
              <LocationSync/>
              <App />
            </>
          ) : (
            <HttpsRedirect>
              <LocationSync/>
              <App />
            </HttpsRedirect>
          )}
        </BrowserRouter>
    </Provider>
  );
}

export default Wrapper;
